import { KalturaPurchaseStatus } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaSubscriptionDependencyType } from '@kaltura-ott/ott-client-types/api/types/KalturaSubscriptionDependencyType';
import {
  KuxPackage,
  KuxPrice,
  KuxPurchaseInfo,
  KuxSubscription,
  assetToType,
  getAssetsByFilter,
  subscriptionsGetProductPrices,
  mediaFileGetSubscription,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import type { DmsStore } from '../dms';
import type { SubscriptionsRepository } from '../subscriptionsRepository';
import type { TransactionStore } from '../transaction/transaction-store';

import { createPackageEntity, getLinearByAssetId } from './creator.entity';
import { Package } from './Package';

export class Subscription {
  @observable data: KuxSubscription;
  @observable assetPackage: Package;
  @observable purchaseDate?: number;
  @observable purchaseStatus?: KalturaPurchaseStatus;
  @observable isPurchasePending?: boolean;
  @observable isPurchased = false;

  constructor(data: KuxSubscription, assetPackage: Package, purchaseInfo?: KuxPurchaseInfo) {
    this.data = data;
    this.assetPackage = assetPackage;
    this.purchaseDate = purchaseInfo?.purchaseDate;
    this.purchaseStatus = purchaseInfo?.purchaseStatus;

    this.isPurchased = this.purchaseStatus === KalturaPurchaseStatus.subscriptionPurchased;

    this.isPurchasePending = this.purchaseStatus === KalturaPurchaseStatus.pendingEntitlement;
  }

  static async findAddonsByFileId(fileId: number): Promise<Subscription[]> {
    const subscriptionResult = await lastValueFrom(mediaFileGetSubscription(fileId));
    const subscriptions = subscriptionResult
      ?.slice()
      .filter(({ dependencyType }) => dependencyType.toLowerCase() === 'addon');

    const productPrices = await lastValueFrom(
      subscriptionsGetProductPrices(subscriptions.map(({ id }) => Number(id)).filter(Boolean)),
    );

    const subscriptionInstances = subscriptions
      .map(async (subscription) => {
        // TODO use one request
        const assetResult = await lastValueFrom(
          getAssetsByFilter({
            kSql: `(and SubscriptionID = '${subscription.id}')`,
          }),
        );

        const kuxPackageAsset: KuxPackage | undefined = assetResult?.objects.length
          ? assetToType(assetResult.objects[0])
          : undefined;

        const packageAsset = kuxPackageAsset ? createPackageEntity(kuxPackageAsset) : undefined;

        if (packageAsset) {
          return new Subscription(
            subscription,
            packageAsset,
            productPrices.find((price) => price.productId === subscription.id.toString()),
          );
        }
      })
      .filter(Boolean);
    return await Promise.all(subscriptionInstances as Promise<Subscription>[]);
  }

  /**
   * Return all addons by assetId
   * @param linearId - number
   */
  static async findAddonsByLinearId(linearId: number): Promise<Subscription[] | undefined> {
    const linears = await getLinearByAssetId([linearId]);
    if (linears && linears[0].mediaFileId) {
      return this.findAddonsByFileId(linears[0].mediaFileId);
    }
  }

  /**
   * @computed packageData: package details
   *
   */

  @computed get packageData(): KuxPackage {
    return this.assetPackage?.data;
  }

  /**
   *  @computed price: subscription's price
   *
   */
  @computed get price(): KuxPrice {
    return this.data.price;
  }

  /**
   * @computed formattedPrice: for showing on subscription page
   *
   */

  @computed get formattedPrice(): string {
    return `${this.price.amount}${this.price.currencySign} / ${this.packageData.priceDescription}`;
  }

  /**
   * method to call for buying (purchasing) current Subscription plan
   *
   */

  @action
  async buy() {
    const transactionStore = container.resolve<TransactionStore>('transactionStore');

    return await transactionStore.purchaseSubscription({
      subscriptionId: this.data.id,
      currency: this.data.price.currency,
      price: this.data.price.amount,
    });
  }

  /**
   * method to call for buying (purchasing) current Subscription plan if we need to call purchase again after pin entered
   *
   */

  @action
  async buyAfterPinEntered() {
    const transactionStore = container.resolve<TransactionStore>('transactionStore');

    return await transactionStore.transactionPurchase({
      subscriptionId: this.data.id,
      currency: this.data.price.currency,
      price: this.data.price.amount,
    });
  }

  /**
   * method to call for cancelling current Subscription plan
   *
   */

  @action
  async cancel() {
    const transactionStore = container.resolve<TransactionStore>('transactionStore');

    return await transactionStore.cancelSubscription(this.data.id);
  }

  get isAdult(): boolean {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    return (
      (dmsStore.dms.subscriptionIds?.adults &&
        dmsStore.dms.subscriptionIds?.adults?.includes(this.data.id.toString())) ||
      false
    );
  }

  @computed get type(): KalturaSubscriptionDependencyType | string {
    return this.data.dependencyType;
  }

  @computed get basePlan(): Subscription | undefined {
    if (this.type === KalturaSubscriptionDependencyType.addon) {
      const subscriptionsRepository = container.resolve<SubscriptionsRepository>('subscriptionsRepository');

      return subscriptionsRepository.getPlanForAddon(this.data.id);
    }
  }
}
